import { useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Tooltip,
  Field,
  Input,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  DialogTrigger,
  tokens,
} from '@fluentui/react-components';
import {
  PersonRegular,
  MailRegular,
  CheckmarkCircleRegular,
  WarningRegular,
  EditRegular,
  PenRegular,
  OpenRegular,
  CopyRegular,
  ReceiptMoneyRegular,
  MoneyRegular,
  DismissCircleRegular,
  PeopleRegular,
  LinkRegular,
  AddRegular,
  ArrowSyncRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import { getOrgAwareXeroUrl } from '../../utils/xero';
import {
  Form8879TypeDisplayNames,
  sharedInvoiceStatusDisplayNames,
  sharedInvoiceStatusColors,
  appointmentTypeDisplayNames,
  appointmentTypeColors,
  requiresRemoteSignature,
  type TaxReturn,
  type Form8879Type,
  type ClientInvoice,
  type AppointmentType,
} from '../../types';
import type { UseMutationResult } from '@tanstack/react-query';

const efileStatusColors: Record<string, string> = {
  pending: 'var(--tl-text-tertiary)',
  submitted: 'var(--tl-brand)',
  accepted: 'var(--tl-success)',
  rejected: 'var(--tl-danger)',
};

export interface BillingTabProps {
  taxReturn: TaxReturn;
  taxReturnId: string;
  sharedInvoice?: ClientInvoice | null;
  linkedReturns?: TaxReturn[];
  appointmentReturns?: TaxReturn[];
  xeroInvoiceAmount: string;
  onXeroInvoiceAmountChange: (val: string) => void;
  onEditStaffOpen: (preparerId: string, reviewerId: string) => void;
  onEditEfileOpen: (efileStatus: string, rejectionReason: string) => void;
  onEditSignatureStatusOpen: (signatureStatus: string) => void;
  onSignatureDialogOpen: () => void;
  onSharedInvoiceDialogOpen: () => void;
  onShowLinkInvoiceDialog: () => void;
  onShowRecordPaymentDialog: () => void;
  onShowCashPaymentDialog: () => void;
  onShowSharedPaymentDialog: () => void;
  resendSignatureMutation: UseMutationResult<unknown, Error, void>;
  voidSignatureMutation: UseMutationResult<unknown, Error, string>;
  generateInvoiceMutation: UseMutationResult<unknown, Error, { amount: number; xeroContactId?: string; forceCreateContact?: boolean }>;
  onGenerateInvoiceClick: (amount: number) => void;
  resolvingXeroContact?: boolean;
  unlinkInvoiceMutation: UseMutationResult<unknown, Error, void>;
  updateInvoiceAmountMutation: UseMutationResult<unknown, Error, number>;
  refreshInvoiceFromXeroMutation: UseMutationResult<unknown, Error, void>;
  voidSharedInvoiceMutation: UseMutationResult<unknown, Error, string>;
  clearCashPaymentMutation: UseMutationResult<unknown, Error, void>;
  addAdditionalInvoiceMutation: UseMutationResult<unknown, Error, { amount: number; description?: string }>;
  voidAdditionalInvoiceMutation: UseMutationResult<unknown, Error, string>;
  copyPaymentLink: () => Promise<void>;
  copiedToClipboard: boolean;
  setCopiedToClipboard: (val: boolean) => void;
}

export function BillingTab({
  taxReturn,
  taxReturnId,
  sharedInvoice,
  linkedReturns,
  appointmentReturns,
  xeroInvoiceAmount,
  onXeroInvoiceAmountChange,
  onEditStaffOpen,
  onEditEfileOpen,
  onEditSignatureStatusOpen,
  onSignatureDialogOpen,
  onSharedInvoiceDialogOpen,
  onShowLinkInvoiceDialog,
  onShowRecordPaymentDialog,
  onShowCashPaymentDialog,
  onShowSharedPaymentDialog,
  resendSignatureMutation,
  voidSignatureMutation,
  generateInvoiceMutation,
  onGenerateInvoiceClick,
  resolvingXeroContact,
  unlinkInvoiceMutation,
  updateInvoiceAmountMutation,
  refreshInvoiceFromXeroMutation,
  voidSharedInvoiceMutation,
  clearCashPaymentMutation,
  addAdditionalInvoiceMutation,
  voidAdditionalInvoiceMutation,
  copyPaymentLink,
  copiedToClipboard,
  setCopiedToClipboard,
}: BillingTabProps) {
  const { t } = useTranslation();

  // E-File status badge label. Rebuilt with useMemo (not a module-scope Record) because it needs
  // t(), following the efileStatusLabels pattern from OverviewTab.tsx.
  const efileStatusLabels: Record<string, string> = useMemo(() => ({
    pending: t('billingTab.efile.statusOptions.pending'),
    submitted: t('billingTab.efile.statusOptions.submitted'),
    accepted: t('billingTab.efile.statusOptions.accepted'),
    rejected: t('billingTab.efile.statusOptions.rejected'),
  }), [t]);

  // Signature badge wording -- deliberately distinct from signatureStatusLabels below: the badge
  // uses the friendlier "Awaiting Signature" for 'sent', while the plain status row uses the
  // literal status word ("Sent").
  const signatureBadgeLabels: Record<string, string> = useMemo(() => ({
    signed: t('billingTab.signature.badgeLabels.signed'),
    sent: t('billingTab.signature.badgeLabels.awaitingSignature'),
    pending: t('billingTab.signature.badgeLabels.pending'),
  }), [t]);

  const signatureStatusLabels: Record<string, string> = useMemo(() => ({
    signed: t('billingTab.signature.statusLabels.signed'),
    sent: t('billingTab.signature.statusLabels.sent'),
    pending: t('billingTab.signature.statusLabels.pending'),
  }), [t]);

  const [showAddInvoiceForm, setShowAddInvoiceForm] = useState(false);
  const [additionalInvoiceAmount, setAdditionalInvoiceAmount] = useState('');
  const [additionalInvoiceDescription, setAdditionalInvoiceDescription] = useState('');
  const [inPersonEsignConfirmOpen, setInPersonEsignConfirmOpen] = useState(false);
  const [editingAmount, setEditingAmount] = useState(false);
  const [editAmountValue, setEditAmountValue] = useState('');

  const handleAddAdditionalInvoice = () => {
    const amount = parseFloat(additionalInvoiceAmount);
    if (amount > 0) {
      addAdditionalInvoiceMutation.mutate(
        { amount, description: additionalInvoiceDescription.trim() || undefined },
        {
          onSuccess: () => {
            setAdditionalInvoiceAmount('');
            setAdditionalInvoiceDescription('');
            setShowAddInvoiceForm(false);
          },
        }
      );
    }
  };

  return (
    <>
      <div className="detail-grid">
        {/* Staff Assignment */}
        <div className="detail-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3><PersonRegular /> {t('billingTab.staffAssignment.heading')}</h3>
            <Button
              appearance="subtle"
              icon={<EditRegular />}
              size="small"
              onClick={() => onEditStaffOpen(taxReturn.preparerId || '', taxReturn.reviewerId || '')}
            />
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('billingTab.staffAssignment.preparerLabel')}</span>
            <span className="detail-value">
              {taxReturn.preparerName ? (
                taxReturn.preparerName
              ) : (
                <Badge appearance="filled" color="warning">{t('billingTab.staffAssignment.unassigned')}</Badge>
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('billingTab.staffAssignment.reviewerLabel')}</span>
            <span className="detail-value">{taxReturn.reviewerName || t('billingTab.staffAssignment.notAssigned')}</span>
          </div>
        </div>

        {/* E-File Status */}
        <div className="detail-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>{t('billingTab.efile.heading')}</h3>
            <Button
              appearance="subtle"
              icon={<EditRegular />}
              size="small"
              onClick={() => onEditEfileOpen(taxReturn.efileStatus || 'pending', taxReturn.efileRejectionReason || '')}
            />
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('billingTab.common.status')}</span>
            <Badge
              appearance="filled"
              style={{
                backgroundColor: efileStatusColors[taxReturn.efileStatus] || 'var(--tl-text-tertiary)',
                color: 'white',
              }}
            >
              {efileStatusLabels[taxReturn.efileStatus] || taxReturn.efileStatus}
            </Badge>
          </div>
          {taxReturn.efileSubmittedDate && (
            <div className="detail-row">
              <span className="detail-label">{t('billingTab.efile.submittedDateLabel')}</span>
              <span className="detail-value">
                {format(new Date(taxReturn.efileSubmittedDate), 'MMM d, yyyy')}
              </span>
            </div>
          )}
          {taxReturn.efileAcceptedDate && (
            <div className="detail-row">
              <span className="detail-label">{t('billingTab.efile.acceptedDateLabel')}</span>
              <span className="detail-value">
                {format(new Date(taxReturn.efileAcceptedDate), 'MMM d, yyyy')}
              </span>
            </div>
          )}
          {taxReturn.efileRejectionReason && (
            <div className="detail-row">
              <span className="detail-label">{t('billingTab.efile.rejectionReasonLabel')}</span>
              <span className="detail-value" style={{ color: 'var(--tl-danger)' }}>
                {/* taxReturn.efileRejectionReason is staff/CRM-entered free text about this
                    specific return -- client-data-derived, not a UI string, so it stays untranslated. */}
                <WarningRegular /> {taxReturn.efileRejectionReason}
              </span>
            </div>
          )}
        </div>

        {/* Signature Status */}
        <div className="detail-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3><PenRegular /> {t('billingTab.signature.heading')}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              appearance="subtle"
              icon={<EditRegular />}
              size="small"
              onClick={() => onEditSignatureStatusOpen(taxReturn.signatureStatus || 'pending')}
            />
            <Badge
              appearance="filled"
              style={{
                backgroundColor: taxReturn.signatureStatus === 'signed' ? 'var(--tl-success)' :
                                taxReturn.signatureStatus === 'sent' ? 'var(--tl-brand)' : 'var(--tl-text-tertiary)',
                color: 'white',
              }}
            >
              {signatureBadgeLabels[taxReturn.signatureStatus] || signatureBadgeLabels.pending}
            </Badge>
            </div>
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('billingTab.common.status')}</span>
            <span className="detail-value" style={{ textTransform: 'capitalize' }}>
              {signatureStatusLabels[taxReturn.signatureStatus] || taxReturn.signatureStatus}
            </span>
          </div>
          {taxReturn.signatureFormType && (
            <div className="detail-row">
              <span className="detail-label">{t('billingTab.signature.formTypeLabel')}</span>
              <span className="detail-value">
                {Form8879TypeDisplayNames[taxReturn.signatureFormType as Form8879Type] || taxReturn.signatureFormType}
              </span>
            </div>
          )}
          {taxReturn.signatureSentDate && (
            <div className="detail-row">
              <span className="detail-label">{t('billingTab.signature.sentLabel')}</span>
              <span className="detail-value">
                {format(new Date(taxReturn.signatureSentDate), 'MMM d, yyyy h:mm a')}
              </span>
            </div>
          )}
          {taxReturn.signatureCompletedDate && (
            <div className="detail-row">
              <span className="detail-label">{t('billingTab.signature.completedLabel')}</span>
              <span className="detail-value">
                {format(new Date(taxReturn.signatureCompletedDate), 'MMM d, yyyy h:mm a')}
              </span>
            </div>
          )}
          {taxReturn.signatureRequestId && (
            <div className="detail-row">
              <span className="detail-label">{t('billingTab.signature.requestIdLabel')}</span>
              <span className="detail-value" style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                {taxReturn.signatureRequestId}
              </span>
            </div>
          )}

          {/* Appointment Type Indicator for Signature */}
          {taxReturn.appointmentType === 'in-person' && (
            <div style={{
              marginTop: '12px',
              padding: '12px 16px',
              backgroundColor: 'var(--tl-brand-bg)',
              borderRadius: '6px',
              border: '1px solid #0078d4',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <CheckmarkCircleRegular style={{ color: 'var(--tl-brand)' }} />
              <span style={{ fontSize: '13px', color: 'var(--tl-brand-strong)' }}>
                <Trans
                  i18nKey="billingTab.signature.inPersonNotice"
                  components={{ strong: <strong /> }}
                />
              </span>
            </div>
          )}
          {requiresRemoteSignature(taxReturn.appointmentType) && taxReturn.signatureStatus === 'pending' && (
            <div style={{
              marginTop: '12px',
              padding: '12px 16px',
              backgroundColor: taxReturn.appointmentType === 'virtual' ? 'var(--tl-purple-bg)' : 'var(--tl-warning-bg)',
              borderRadius: '6px',
              border: `1px solid ${appointmentTypeColors[taxReturn.appointmentType as AppointmentType] || 'var(--tl-purple)'}`,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <MailRegular style={{ color: appointmentTypeColors[taxReturn.appointmentType as AppointmentType] || 'var(--tl-purple)' }} />
              <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                <Trans
                  i18nKey="billingTab.signature.remoteNotice"
                  values={{ appointmentType: appointmentTypeDisplayNames[taxReturn.appointmentType as AppointmentType] }}
                  components={{ strong: <strong /> }}
                />
              </span>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {taxReturn.signatureStatus === 'pending' && taxReturn.status === 'ready_for_signature' && requiresRemoteSignature(taxReturn.appointmentType) && (
              <Button
                appearance="primary"
                icon={<PenRegular />}
                size="small"
                onClick={onSignatureDialogOpen}
              >
                {t('billingTab.signature.sendForSignature')}
              </Button>
            )}
            {taxReturn.signatureStatus === 'pending' && taxReturn.status === 'ready_for_signature' && taxReturn.appointmentType === 'in-person' && (
              <>
                <Badge
                  appearance="filled"
                  style={{
                    backgroundColor: '#0078d4',
                    color: 'white',
                    padding: '8px 12px',
                    fontSize: '13px',
                  }}
                >
                  {t('billingTab.signature.signOnSiteBadge')}
                </Badge>
                <Button
                  appearance="outline"
                  icon={<PenRegular />}
                  size="small"
                  onClick={() => setInPersonEsignConfirmOpen(true)}
                >
                  {t('billingTab.signature.sendForESignature')}
                </Button>
              </>
            )}
            {taxReturn.signatureStatus === 'sent' && (
              <>
                <Button
                  appearance="secondary"
                  icon={<MailRegular />}
                  size="small"
                  onClick={() => resendSignatureMutation.mutate()}
                  disabled={resendSignatureMutation.isPending}
                >
                  {resendSignatureMutation.isPending ? t('billingTab.signature.sending') : t('billingTab.signature.resendNotification')}
                </Button>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => {
                    if (confirm(t('billingTab.signature.voidConfirm'))) {
                      // 'Voided by staff' is persisted to the backend as a permanent audit note --
                      // deliberately NOT t(), so it reads the same for every future viewer
                      // regardless of their own language toggle.
                      voidSignatureMutation.mutate('Voided by staff');
                    }
                  }}
                  disabled={voidSignatureMutation.isPending}
                >
                  {voidSignatureMutation.isPending ? t('billingTab.common.voiding') : t('billingTab.signature.voidRequest')}
                </Button>
              </>
            )}
          </div>
          {(resendSignatureMutation.isError || voidSignatureMutation.isError) && (
            <div style={{ color: 'var(--tl-danger)', fontSize: '13px', marginTop: '8px' }}>
              <WarningRegular /> {resendSignatureMutation.error?.message || voidSignatureMutation.error?.message || t('billingTab.signature.genericError')}
            </div>
          )}
          {resendSignatureMutation.isSuccess && (
            <div style={{ color: 'var(--tl-success)', fontSize: '13px', marginTop: '8px' }}>
              <CheckmarkCircleRegular /> {t('billingTab.signature.resendSuccess')}
            </div>
          )}
        </div>

        {/* Xero Invoice */}
        <div className="detail-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3><ReceiptMoneyRegular /> {t('billingTab.invoice.heading')}</h3>
            {sharedInvoice && (
              <Badge
                appearance="filled"
                style={{ backgroundColor: sharedInvoiceStatusColors[sharedInvoice.status] }}
                icon={sharedInvoice.status === 'paid' ? <CheckmarkCircleRegular /> : undefined}
              >
                {t('billingTab.invoice.sharedBadge', { status: sharedInvoiceStatusDisplayNames[sharedInvoice.status] })}
              </Badge>
            )}
            {!sharedInvoice && taxReturn.clientInvoice?.status === 'paid' && (
              <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>
                {t('billingTab.common.paid')}
              </Badge>
            )}
            {!sharedInvoice && taxReturn.clientInvoice?.status === 'generated' && (
              <Badge appearance="filled" color="brand" icon={<CheckmarkCircleRegular />}>
                {t('billingTab.common.generated')}
              </Badge>
            )}
          </div>

          {/* Shared Invoice Display */}
          {sharedInvoice && (
            <div style={{ marginTop: '12px' }}>
              <div style={{
                padding: '12px',
                backgroundColor: 'var(--tl-brand-bg)',
                borderRadius: '8px',
                border: '1px solid #0078d4',
                marginBottom: '12px',
              }}>
                <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px', color: 'var(--tl-brand)' }}>
                  <PeopleRegular style={{ marginRight: '8px' }} />
                  {t('billingTab.invoice.shared.countHeading', { count: sharedInvoice.taxReturnAllocations.length })}
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('billingTab.common.invoiceNumber')}</span>
                  <span className="detail-value" style={{ fontWeight: 600 }}>
                    {sharedInvoice.invoiceNumber || t('billingTab.common.pending')}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('billingTab.invoice.shared.totalAmountLabel')}</span>
                  <span className="detail-value" style={{ fontWeight: 600 }}>
                    ${sharedInvoice.totalAmount.toFixed(2)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('billingTab.invoice.shared.amountDueLabel')}</span>
                  <span className="detail-value" style={{ fontWeight: 600, color: sharedInvoice.amountDue > 0 ? 'var(--tl-danger)' : 'var(--tl-success)' }}>
                    ${sharedInvoice.amountDue.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* This return's allocation */}
              {(() => {
                const allocation = sharedInvoice.taxReturnAllocations.find(a => a.taxReturnId === taxReturnId);
                if (!allocation) return null;
                return (
                  <div style={{
                    padding: '12px',
                    backgroundColor: allocation.isPaid ? 'var(--tl-success-bg)' : 'var(--tl-warning-bg)',
                    borderRadius: '8px',
                    border: `1px solid ${allocation.isPaid ? 'var(--tl-success)' : 'var(--tl-warning)'}`,
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
                      {t('billingTab.invoice.shared.allocationHeading')}
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('billingTab.invoice.shared.allocatedAmountLabel')}</span>
                      <span className="detail-value" style={{ fontWeight: 600 }}>
                        ${allocation.allocatedAmount.toFixed(2)} ({allocation.allocationPercentage.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('billingTab.invoice.shared.paidAmountLabel')}</span>
                      <span className="detail-value" style={{ fontWeight: 600, color: allocation.isPaid ? 'var(--tl-success)' : 'var(--tl-text-tertiary)' }}>
                        ${allocation.paidAmount.toFixed(2)}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('billingTab.common.status')}</span>
                      <Badge appearance="filled" color={allocation.isPaid ? 'success' : 'warning'}>
                        {allocation.isPaid ? t('billingTab.common.paid') : t('billingTab.common.pending')}
                      </Badge>
                    </div>
                  </div>
                );
              })()}

              {/* Other returns in this shared invoice */}
              {sharedInvoice.taxReturnAllocations.filter(a => a.taxReturnId !== taxReturnId).length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px', color: 'var(--tl-text-secondary)' }}>
                    {t('billingTab.invoice.shared.otherReturnsHeading')}
                  </div>
                  {sharedInvoice.taxReturnAllocations
                    .filter(a => a.taxReturnId !== taxReturnId)
                    .map(allocation => (
                      <div key={allocation.taxReturnId} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px',
                        backgroundColor: 'var(--tl-bg-subtle)',
                        borderRadius: '4px',
                        marginBottom: '4px',
                      }}>
                        {/* allocation.clientName and allocation.returnType are client-data-derived
                            (the other return's own client/type), not UI strings -- left untranslated. */}
                        <Link to={`/tax-returns/${allocation.taxReturnId}`} style={{ color: 'var(--tl-brand)', textDecoration: 'none' }}>
                          {allocation.clientName || t('billingTab.invoice.shared.unknownClient')} ({allocation.returnType || t('billingTab.invoice.shared.returnFallback')})
                        </Link>
                        <span style={{ fontSize: '13px' }}>
                          ${allocation.allocatedAmount.toFixed(2)}
                          <Badge
                            appearance="outline"
                            color={allocation.isPaid ? 'success' : 'warning'}
                            style={{ marginLeft: '8px' }}
                          >
                            {allocation.isPaid ? t('billingTab.common.paid') : t('billingTab.common.pending')}
                          </Badge>
                        </span>
                      </div>
                    ))}
                </div>
              )}

              {/* Payment Link & Xero Actions */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                {sharedInvoice.paymentUrl && (
                  <Button
                    appearance="secondary"
                    icon={<CopyRegular />}
                    onClick={async () => {
                      await navigator.clipboard.writeText(sharedInvoice.paymentUrl!);
                      setCopiedToClipboard(true);
                      setTimeout(() => setCopiedToClipboard(false), 2000);
                    }}
                  >
                    {copiedToClipboard ? t('billingTab.common.copied') : t('billingTab.common.copyPaymentLink')}
                  </Button>
                )}
                {sharedInvoice.xeroUrl && (
                  <Button
                    as="a"
                    appearance="subtle"
                    icon={<OpenRegular />}
                    href={getOrgAwareXeroUrl(sharedInvoice.xeroUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('billingTab.common.openInXero')}
                  </Button>
                )}
                {/* Record Payment on Shared Invoice */}
                {sharedInvoice.status !== 'paid' && sharedInvoice.status !== 'void' && (
                  <Button
                    appearance="secondary"
                    icon={<MoneyRegular />}
                    onClick={onShowSharedPaymentDialog}
                  >
                    {t('billingTab.common.recordPayment')}
                  </Button>
                )}
                {/* Unlink This Return from Shared Invoice */}
                {sharedInvoice.status !== 'void' && (
                  <Button
                    appearance="secondary"
                    icon={<LinkRegular />}
                    onClick={() => {
                      if (confirm(t('billingTab.invoice.shared.unlinkConfirm'))) {
                        unlinkInvoiceMutation.mutate();
                      }
                    }}
                    disabled={unlinkInvoiceMutation.isPending}
                  >
                    {unlinkInvoiceMutation.isPending ? t('billingTab.common.unlinking') : t('billingTab.invoice.shared.unlinkFromInvoice')}
                  </Button>
                )}
                {/* Void Shared Invoice */}
                {sharedInvoice.status !== 'void' && (
                  <Button
                    appearance="subtle"
                    icon={<DismissCircleRegular />}
                    onClick={() => {
                      if (confirm(t('billingTab.invoice.shared.voidConfirm', { count: sharedInvoice.taxReturnAllocations.length }))) {
                        voidSharedInvoiceMutation.mutate(sharedInvoice.id);
                      }
                    }}
                    disabled={voidSharedInvoiceMutation.isPending}
                  >
                    {voidSharedInvoiceMutation.isPending ? t('billingTab.common.voiding') : t('billingTab.invoice.shared.voidSharedInvoice')}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Not Generated State - Show option to create shared invoice or individual invoice */}
          {!sharedInvoice && (!taxReturn.clientInvoice?.status || taxReturn.clientInvoice.status === 'not_generated') && (
            <div style={{ marginTop: '12px' }}>
              <div className="detail-row">
                <span className="detail-label">{t('billingTab.common.status')}</span>
                <Badge appearance="outline" color="warning">{t('billingTab.invoice.notGenerated.notGeneratedBadge')}</Badge>
              </div>

              {/* Show Create Shared Invoice option */}
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: 'var(--tl-brand-bg)',
                borderRadius: '8px',
                border: '1px solid #0078d4',
              }}>
                <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
                  <PeopleRegular style={{ marginRight: '8px' }} />
                  {t('billingTab.invoice.notGenerated.createSharedInvoice')}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', marginBottom: '12px' }}>
                  {(appointmentReturns && appointmentReturns.length > 0) || (linkedReturns && linkedReturns.length > 0)
                    ? t('billingTab.invoice.notGenerated.createSharedDescriptionMultiple', { count: (appointmentReturns?.length || 0) + (linkedReturns?.length || 0) })
                    : t('billingTab.invoice.notGenerated.createSharedDescriptionSingle')}
                </div>
                <Button
                  appearance="primary"
                  icon={<ReceiptMoneyRegular />}
                  onClick={onSharedInvoiceDialogOpen}
                >
                  {t('billingTab.invoice.notGenerated.createSharedInvoice')}
                </Button>
              </div>

              {/* Regular single invoice generation */}
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px', color: 'var(--tl-text-secondary)' }}>
                  {t('billingTab.invoice.notGenerated.orIndividual')}
                </div>
                <Field label={t('billingTab.common.amount')} style={{ marginTop: '12px' }}>
                  <Input
                    type="number"
                    value={xeroInvoiceAmount || (taxReturn.extractedInvoiceData?.totalAmount?.toString() || '')}
                    onChange={(_, data) => onXeroInvoiceAmountChange(data.value)}
                    placeholder={t('billingTab.common.amountPlaceholder')}
                    contentBefore={<span>$</span>}
                  />
                </Field>
                {taxReturn.extractedInvoiceData?.totalAmount && !xeroInvoiceAmount && (
                  <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', marginTop: '4px' }}>
                    {t('billingTab.invoice.notGenerated.prefilledNote')}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <Button
                    appearance="primary"
                    icon={<ReceiptMoneyRegular />}
                    disabled={!xeroInvoiceAmount && !taxReturn.extractedInvoiceData?.totalAmount || generateInvoiceMutation.isPending || resolvingXeroContact}
                    onClick={() => {
                      const amount = parseFloat(xeroInvoiceAmount || taxReturn.extractedInvoiceData?.totalAmount?.toString() || '0');
                      if (amount > 0) {
                        onGenerateInvoiceClick(amount);
                      }
                    }}
                  >
                    {resolvingXeroContact ? t('billingTab.invoice.notGenerated.resolvingContact') : generateInvoiceMutation.isPending ? t('billingTab.invoice.notGenerated.generating') : t('billingTab.invoice.notGenerated.generateInvoice')}
                  </Button>
                  <Button
                    appearance="secondary"
                    icon={<LinkRegular />}
                    onClick={onShowLinkInvoiceDialog}
                  >
                    {t('billingTab.invoice.notGenerated.linkExistingInvoice')}
                  </Button>
                </div>
                {generateInvoiceMutation.isError && (
                  <div style={{ color: 'var(--tl-danger)', fontSize: '13px', marginTop: '8px' }}>
                    <WarningRegular />{' '}
                    {(generateInvoiceMutation.error as Error)?.message
                      || t('billingTab.invoice.notGenerated.generateError')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generated State (only show if no shared invoice) */}
          {!sharedInvoice && taxReturn.clientInvoice?.status === 'generated' && (
            <div style={{ marginTop: '12px' }}>
              <div className="detail-row">
                <span className="detail-label">{t('billingTab.common.invoiceNumber')}</span>
                <span className="detail-value" style={{ fontWeight: 600 }}>
                  {taxReturn.clientInvoice.invoiceNumber}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('billingTab.common.amount')}</span>
                {editingAmount ? (
                  <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Input
                      type="number"
                      size="small"
                      contentBefore={<span>$</span>}
                      value={editAmountValue}
                      onChange={(_, d) => setEditAmountValue(d.value)}
                      style={{ width: '120px' }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const newAmount = parseFloat(editAmountValue);
                          if (Number.isFinite(newAmount) && newAmount > 0) {
                            updateInvoiceAmountMutation.mutate(newAmount, {
                              onSuccess: () => setEditingAmount(false),
                            });
                          }
                        } else if (e.key === 'Escape') {
                          setEditingAmount(false);
                        }
                      }}
                    />
                    <Button
                      appearance="primary"
                      size="small"
                      disabled={updateInvoiceAmountMutation.isPending || !editAmountValue || !Number.isFinite(parseFloat(editAmountValue)) || parseFloat(editAmountValue) <= 0}
                      onClick={() => {
                        const newAmount = parseFloat(editAmountValue);
                        if (Number.isFinite(newAmount) && newAmount > 0) {
                          updateInvoiceAmountMutation.mutate(newAmount, {
                            onSuccess: () => setEditingAmount(false),
                          });
                        }
                      }}
                    >
                      {updateInvoiceAmountMutation.isPending ? t('billingTab.common.saving') : t('billingTab.common.save')}
                    </Button>
                    <Button
                      appearance="subtle"
                      size="small"
                      onClick={() => setEditingAmount(false)}
                    >
                      {t('common.cancel')}
                    </Button>
                  </span>
                ) : (
                  <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 600, color: 'var(--tl-success)' }}>
                      ${taxReturn.clientInvoice.amount?.toFixed(2)}
                    </span>
                    <Tooltip content={t('billingTab.invoice.generated.editAmountTooltip')} relationship="label">
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<EditRegular />}
                        onClick={() => {
                          setEditAmountValue(taxReturn.clientInvoice.amount?.toFixed(2) ?? '');
                          setEditingAmount(true);
                        }}
                      />
                    </Tooltip>
                  </span>
                )}
              </div>
              {taxReturn.clientInvoice.generatedDate && (
                <div className="detail-row">
                  <span className="detail-label">{t('billingTab.common.generated')}</span>
                  <span className="detail-value">
                    {format(new Date(taxReturn.clientInvoice.generatedDate), 'MMM d, yyyy')}
                  </span>
                </div>
              )}

              {/* Payment Link Section */}
              {taxReturn.clientInvoice.paymentUrl && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: 'var(--tl-brand-bg)',
                  borderRadius: '8px',
                  border: '1px solid #0078d4',
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>{t('billingTab.invoice.generated.paymentLinkHeading')}</div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    border: '1px solid var(--tl-border)',
                  }}>
                    <span style={{
                      flex: 1,
                      fontSize: '13px',
                      color: 'var(--tl-brand)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {taxReturn.clientInvoice.paymentUrl}
                    </span>
                    <Tooltip content={copiedToClipboard ? t('billingTab.common.copied') : t('billingTab.invoice.generated.copyLinkTooltip')} relationship="label">
                      <Button
                        appearance="subtle"
                        icon={<CopyRegular />}
                        size="small"
                        onClick={copyPaymentLink}
                      />
                    </Tooltip>
                    <Tooltip content={t('billingTab.invoice.generated.openPaymentPageTooltip')} relationship="label">
                      <Button
                        as="a"
                        appearance="subtle"
                        icon={<OpenRegular />}
                        size="small"
                        href={taxReturn.clientInvoice.paymentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    </Tooltip>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                <Button
                  appearance="primary"
                  icon={<MoneyRegular />}
                  onClick={onShowRecordPaymentDialog}
                >
                  {t('billingTab.common.recordPayment')}
                </Button>
                {taxReturn.clientInvoice.paymentUrl && (
                  <Button
                    appearance="secondary"
                    icon={<CopyRegular />}
                    onClick={copyPaymentLink}
                  >
                    {copiedToClipboard ? t('billingTab.common.copied') : t('billingTab.common.copyPaymentLink')}
                  </Button>
                )}
                {taxReturn.clientInvoice.xeroUrl && (
                  <Button
                    as="a"
                    appearance="subtle"
                    icon={<OpenRegular />}
                    href={getOrgAwareXeroUrl(taxReturn.clientInvoice.xeroUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('billingTab.common.openInXero')}
                  </Button>
                )}
                {taxReturn.invoiceId && (
                  <Button
                    appearance="subtle"
                    icon={<ArrowSyncRegular />}
                    onClick={() => refreshInvoiceFromXeroMutation.mutate()}
                    disabled={refreshInvoiceFromXeroMutation.isPending}
                  >
                    {refreshInvoiceFromXeroMutation.isPending ? t('billingTab.common.refreshing') : t('billingTab.common.refreshFromXero')}
                  </Button>
                )}
                <Button
                  appearance="subtle"
                  icon={<LinkRegular />}
                  onClick={() => {
                    if (confirm(t('billingTab.invoice.generated.unlinkConfirm'))) {
                      unlinkInvoiceMutation.mutate();
                    }
                  }}
                  disabled={unlinkInvoiceMutation.isPending}
                >
                  {unlinkInvoiceMutation.isPending ? t('billingTab.common.unlinking') : t('billingTab.invoice.generated.unlinkInvoiceButton')}
                </Button>
              </div>
            </div>
          )}

          {/* Paid State (only show if no shared invoice) */}
          {!sharedInvoice && taxReturn.clientInvoice?.status === 'paid' && (
            <div style={{ marginTop: '12px' }}>
              <div className="detail-row">
                <span className="detail-label">{t('billingTab.common.invoiceNumber')}</span>
                <span className="detail-value" style={{ fontWeight: 600 }}>
                  {taxReturn.clientInvoice.invoiceNumber}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('billingTab.common.amount')}</span>
                <span className="detail-value" style={{ fontWeight: 600, color: 'var(--tl-success)' }}>
                  ${taxReturn.clientInvoice.amount?.toFixed(2)}
                </span>
              </div>
              {taxReturn.clientInvoice.paidDate && (
                <div className="detail-row">
                  <span className="detail-label">{t('billingTab.common.paid')}</span>
                  <span className="detail-value">
                    {format(new Date(taxReturn.clientInvoice.paidDate), 'MMM d, yyyy')}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                {taxReturn.clientInvoice.xeroUrl && (
                  <Button
                    as="a"
                    appearance="subtle"
                    icon={<OpenRegular />}
                    size="small"
                    href={getOrgAwareXeroUrl(taxReturn.clientInvoice.xeroUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('billingTab.invoice.paidState.viewInXero')}
                  </Button>
                )}
                {taxReturn.invoiceId && (
                  <Button
                    appearance="subtle"
                    icon={<ArrowSyncRegular />}
                    size="small"
                    onClick={() => refreshInvoiceFromXeroMutation.mutate()}
                    disabled={refreshInvoiceFromXeroMutation.isPending}
                  >
                    {refreshInvoiceFromXeroMutation.isPending ? t('billingTab.common.refreshing') : t('billingTab.common.refreshFromXero')}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Additional Invoices Section */}
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--tl-border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: tokens.fontSizeBase300, fontWeight: 600 }}>
                <ReceiptMoneyRegular style={{ marginRight: '8px' }} />
                {t('billingTab.invoice.additional.heading')}
              </h4>
              <Button
                appearance="subtle"
                icon={<AddRegular />}
                size="small"
                onClick={() => setShowAddInvoiceForm(!showAddInvoiceForm)}
              >
                {t('billingTab.invoice.additional.addInvoice')}
              </Button>
            </div>

            {/* Total Invoiced Amount (show when there are additional invoices) */}
            {taxReturn.additionalInvoices && taxReturn.additionalInvoices.length > 0 && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: 'var(--tl-brand-bg)',
                borderRadius: '6px',
                border: '1px solid #0078d4',
                marginBottom: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ fontSize: '13px', fontWeight: 500 }}>{t('billingTab.invoice.additional.totalInvoicedLabel')}</span>
                <span style={{ fontSize: tokens.fontSizeBase300, fontWeight: 600, color: 'var(--tl-brand)' }}>
                  ${(taxReturn.totalInvoicedAmount ?? 0).toFixed(2)}
                </span>
              </div>
            )}

            {/* List of additional invoices */}
            {taxReturn.additionalInvoices && taxReturn.additionalInvoices.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                {taxReturn.additionalInvoices.map((invoice, idx) => (
                  <div
                    key={invoice.id || idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: 'var(--tl-bg-subtle)',
                      borderRadius: '6px',
                      border: '1px solid var(--tl-border)',
                    }}
                  >
                    <div>
                      {/* invoice.description is a staff-entered free-text note about this specific
                          additional invoice -- client-data-derived, not a UI string, so the fallback
                          only is translated. */}
                      <div style={{ fontWeight: 500, fontSize: '13px' }}>
                        {invoice.description || t('billingTab.invoice.additional.descriptionFallback', { index: idx + 1 })}
                      </div>
                      <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                        ${invoice.amount?.toFixed(2)}
                        {invoice.generatedDate && (
                          <> &middot; {format(new Date(invoice.generatedDate), 'MMM d, yyyy')}</>
                        )}
                        {invoice.invoiceNumber && (
                          <> &middot; #{invoice.invoiceNumber}</>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Badge
                        appearance="filled"
                        color={invoice.status === 'paid' ? 'success' : invoice.status === 'generated' ? 'brand' : 'warning'}
                        size="small"
                      >
                        {invoice.status === 'paid' ? t('billingTab.common.paid') : invoice.status === 'generated' ? t('billingTab.common.generated') : t('billingTab.common.pending')}
                      </Badge>
                      {invoice.id && invoice.status !== 'paid' && (
                        <Button
                          appearance="subtle"
                          icon={<DismissCircleRegular />}
                          size="small"
                          onClick={() => {
                            if (confirm(t('billingTab.invoice.additional.voidConfirm'))) {
                              voidAdditionalInvoiceMutation.mutate(invoice.id!);
                            }
                          }}
                          disabled={voidAdditionalInvoiceMutation.isPending}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No additional invoices message */}
            {(!taxReturn.additionalInvoices || taxReturn.additionalInvoices.length === 0) && !showAddInvoiceForm && (
              <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', marginBottom: '12px' }}>
                {t('billingTab.invoice.additional.noInvoicesMessage')}
              </div>
            )}

            {/* Add Additional Invoice Form */}
            {showAddInvoiceForm && (
              <div style={{
                padding: '12px',
                backgroundColor: 'var(--tl-bg-page)',
                borderRadius: '8px',
                border: '1px solid var(--tl-border)',
              }}>
                <Field label={t('billingTab.common.amount')} required style={{ marginBottom: '12px' }}>
                  <Input
                    type="number"
                    value={additionalInvoiceAmount}
                    onChange={(_, data) => setAdditionalInvoiceAmount(data.value)}
                    placeholder={t('billingTab.common.amountPlaceholder')}
                    contentBefore={<span>$</span>}
                  />
                </Field>
                <Field label={t('billingTab.common.description')} style={{ marginBottom: '12px' }}>
                  <Input
                    value={additionalInvoiceDescription}
                    onChange={(_, data) => setAdditionalInvoiceDescription(data.value)}
                    placeholder={t('billingTab.invoice.additional.descriptionPlaceholder')}
                  />
                </Field>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    appearance="primary"
                    size="small"
                    disabled={!additionalInvoiceAmount || parseFloat(additionalInvoiceAmount) <= 0 || addAdditionalInvoiceMutation.isPending}
                    onClick={handleAddAdditionalInvoice}
                  >
                    {addAdditionalInvoiceMutation.isPending ? t('billingTab.invoice.additional.adding') : t('billingTab.invoice.additional.addInvoice')}
                  </Button>
                  <Button
                    appearance="secondary"
                    size="small"
                    onClick={() => {
                      setShowAddInvoiceForm(false);
                      setAdditionalInvoiceAmount('');
                      setAdditionalInvoiceDescription('');
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
                {addAdditionalInvoiceMutation.isError && (
                  <div style={{ color: 'var(--tl-danger)', fontSize: '13px', marginTop: '8px' }}>
                    <WarningRegular /> {t('billingTab.invoice.additional.addError')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cash Payment Section (separate from invoice) */}
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--tl-border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: tokens.fontSizeBase300, fontWeight: 600 }}>
                <MoneyRegular style={{ marginRight: '8px' }} />
                {t('billingTab.invoice.cash.heading')}
              </h4>
            </div>

            {taxReturn.cashPaymentAmount && taxReturn.cashPaymentAmount > 0 ? (
              <div style={{
                padding: '12px',
                backgroundColor: 'var(--tl-success-bg)',
                borderRadius: '8px',
                border: '1px solid #107c10',
              }}>
                <div className="detail-row">
                  <span className="detail-label">{t('billingTab.common.amount')}</span>
                  <span className="detail-value" style={{ fontWeight: 600, color: 'var(--tl-success)' }}>
                    ${taxReturn.cashPaymentAmount.toFixed(2)}
                  </span>
                </div>
                {taxReturn.cashPaymentDate && (
                  <div className="detail-row">
                    <span className="detail-label">{t('billingTab.common.date')}</span>
                    <span className="detail-value">
                      {format(new Date(taxReturn.cashPaymentDate), 'MMM d, yyyy h:mm a')}
                    </span>
                  </div>
                )}
                {taxReturn.cashPaymentReference && (
                  <div className="detail-row">
                    <span className="detail-label">{t('billingTab.common.reference')}</span>
                    <span className="detail-value">{taxReturn.cashPaymentReference}</span>
                  </div>
                )}
                {taxReturn.cashPaymentRecordedBy && (
                  <div className="detail-row">
                    <span className="detail-label">{t('billingTab.common.recordedBy')}</span>
                    <span className="detail-value">{taxReturn.cashPaymentRecordedBy}</span>
                  </div>
                )}
                <Button
                  appearance="subtle"
                  size="small"
                  style={{ marginTop: '8px' }}
                  onClick={() => {
                    if (confirm(t('billingTab.invoice.cash.clearConfirm'))) {
                      clearCashPaymentMutation.mutate();
                    }
                  }}
                  disabled={clearCashPaymentMutation.isPending}
                >
                  {clearCashPaymentMutation.isPending ? t('billingTab.invoice.cash.clearing') : t('billingTab.invoice.cash.clearPayment')}
                </Button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', marginBottom: '12px' }}>
                  {t('billingTab.invoice.cash.description')}
                </div>
                <Button
                  appearance="secondary"
                  icon={<MoneyRegular />}
                  onClick={onShowCashPaymentDialog}
                >
                  {t('billingTab.invoice.cash.recordPayment')}
                </Button>
              </div>
            )}
          </div>

          {/* Preparer Fee (separate from client invoice) */}
          {taxReturn.preparerBillAmount !== undefined && taxReturn.preparerBillAmount > 0 && (
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--tl-border)' }}>
              <div className="detail-row">
                <span className="detail-label">{t('billingTab.preparerFeeLabel')}</span>
                <span className="detail-value" style={{ color: 'var(--tl-brand)' }}>
                  ${taxReturn.preparerBillAmount.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* In-Person E-Sign Confirmation Dialog */}
      <Dialog open={inPersonEsignConfirmOpen} onOpenChange={(_, data) => setInPersonEsignConfirmOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('billingTab.dialogs.inPersonEsign.title')}</DialogTitle>
            <DialogContent>
              <p>
                {t('billingTab.dialogs.inPersonEsign.body')}
              </p>
              <p style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                {t('billingTab.dialogs.inPersonEsign.note')}
              </p>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{t('common.cancel')}</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                icon={<PenRegular />}
                onClick={() => {
                  setInPersonEsignConfirmOpen(false);
                  onSignatureDialogOpen();
                }}
              >
                {t('billingTab.dialogs.inPersonEsign.confirmButton')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
